import { loadSofaElement } from '../DynamicLoadSofaShape'

export interface ChangeableProperties {
  width?: boolean
  height?: boolean
  length?: boolean
  seat_height?: boolean
  seat_depth?: boolean
  seat_width?: boolean
  corner_radius?: boolean
  armrest_width?: boolean
  backrest_width?: boolean
  number_of_big_pillows?: boolean
  number_of_small_pillows?: boolean
  spread_of_small_pillows?: boolean
  spread_of_big_pillows?: boolean
  size_of_big_pillow?: boolean
  size_of_pillow?: boolean
  extension_type?: boolean
  backrest_type?: boolean
  angle?: boolean
  covered_side?: boolean
  extendable_part_length?: boolean
  corner_part_length?: boolean
}

// Default changeable properties (fallback when shape doesn't have getDefaultSettings)
const DEFAULT_CHANGEABLE_PROPERTIES: ChangeableProperties = {
  width: true,
  height: true,
  length: true,
  // seat_height: true,
  // seat_depth: true,
  // seat_width: true,
  // corner_radius: true,
  armrest_width: true,
  backrest_width: true,
  // number_of_big_pillows: true,
  // number_of_small_pillows: true,
  // spread_of_small_pillows: true,
  // spread_of_big_pillows: true,
  // size_of_big_pillow: true,
  // size_of_pillow: true,
  extension_type: true,
  backrest_type: true,
}

/**
 * Admin-side corrections to a shape's own getDefaultSettings (the shape files
 * are synced to the B2B portal, so they're not edited here). LCHL/LCHR draw
 * their backrest band and armrest strip from backrestWidth/armrestWidth, but
 * their settings hide both inputs.
 */
const CHANGEABLE_OVERRIDES: Record<string, ChangeableProperties> = {
  LCHL: { armrest_width: true, backrest_width: true },
  LCHR: { armrest_width: true, backrest_width: true },
}

/**
 * Defaults to prefill alongside the overrides above — the values the shape
 * component falls back to (armrestWidth = 10, backrestWidth = 20), so the
 * create form's inputs match the drawing.
 */
const DEFAULT_DIMENSION_OVERRIDES: Record<
  string,
  { armrest_width?: number; backrest_width?: number }
> = {
  LCHL: { armrest_width: 10, backrest_width: 20 },
  LCHR: { armrest_width: 10, backrest_width: 20 },
}

export const applyChangeableOverrides = (
  shapeType: string,
  properties: ChangeableProperties
): ChangeableProperties =>
  CHANGEABLE_OVERRIDES[shapeType]
    ? { ...properties, ...CHANGEABLE_OVERRIDES[shapeType] }
    : properties

export const getDefaultDimensionOverrides = (shapeType: string) =>
  DEFAULT_DIMENSION_OVERRIDES[shapeType] ?? {}

/**
 * Get changeable properties for a sofa shape type
 * If the shape has getDefaultSettings function, use its changeableProperties
 * Otherwise, return default properties (all fields changeable)
 */
export const getShapeChangeableProperties = async (
  shapeType: string
): Promise<ChangeableProperties> => {
  try {
    const moduleExports = await loadSofaElement(shapeType)

    if (moduleExports?.getDefaultSettings) {
      const settings = moduleExports.getDefaultSettings()
      return applyChangeableOverrides(
        shapeType,
        settings.changeableProperties || DEFAULT_CHANGEABLE_PROPERTIES
      )
    }

    // Fallback to default properties if shape doesn't have getDefaultSettings
    return applyChangeableOverrides(shapeType, DEFAULT_CHANGEABLE_PROPERTIES)
  } catch (error) {
    console.error('Error loading shape configuration:', error)
    // Return default properties on error
    return DEFAULT_CHANGEABLE_PROPERTIES
  }
}
